import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { cn } from "@/lib/utils";
import {
  Check,
  CheckCircle2,
  Clock,
  Eye,
  Hourglass,
  Loader,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { WarningIcon } from "./icons";

type StatusIconVariant = "sm" | "md";

type Props = {
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  color?: string;
  variant?: StatusIconVariant;
};

const getVariantClasses = (variant: StatusIconVariant = "md") => {
  const variants = {
    sm: {
      container: "gap-1 text-[11px] font-normal px-1 py-0.5 leading-none",
      icon: "h-2.5 w-2.5",
    },
    md: {
      container: "gap-1.5 text-xs font-semibold py-0.5 px-2",
      icon: "h-3.5 w-3.5",
    },
  };
  return variants[variant];
};

export const ReviewedIcon = ({ className, variant = "md" }: Props) => {
  const { t } = useTranslation();
  const variantClasses = getVariantClasses(variant);

  return (
    <div
      className={cn(
        "flex flex-row items-center justify-start overflow-hidden whitespace-nowrap text-ellipsis rounded-md bg-green-100 border border-green-200 text-green-800",
        variantClasses.container,
        className,
      )}
    >
      <Check
        className={cn(variantClasses.icon, "flex-shrink-0")}
        aria-hidden="true"
      />
      {t("invoice_statuses.reviewed")}
    </div>
  );
};

export const ToReviewIcon = ({ className, variant = "md" }: Props) => {
  const { t } = useTranslation();
  const variantClasses = getVariantClasses(variant);

  return (
    <div
      className={cn(
        "flex flex-row items-center justify-start overflow-hidden whitespace-nowrap text-ellipsis rounded-md bg-orange-100 border border-orange-200 text-orange-800",
        variantClasses.container,
        className,
      )}
    >
      <Eye
        className={cn(variantClasses.icon, "flex-shrink-0")}
        aria-hidden="true"
      />
      {t("invoice_statuses.unreviewed")}
    </div>
  );
};

export const ProcessingIcon = ({ className, variant = "md" }: Props) => {
  const { t } = useTranslation();
  const variantClasses = getVariantClasses(variant);

  return (
    <div
      className={cn(
        "flex flex-row items-center justify-start overflow-hidden whitespace-nowrap text-ellipsis rounded-md bg-gray-100 border border-gray-200 text-gray-800",
        variantClasses.container,
        className,
      )}
    >
      <Clock
        className={cn(variantClasses.icon, "flex-shrink-0")}
        aria-hidden="true"
      />
      {t("invoice_statuses.new")}
    </div>
  );
};

export const ExportingIcon = ({ className, variant = "md" }: Props) => {
  const { t } = useTranslation();
  const variantClasses = getVariantClasses(variant);

  return (
    <div
      className={cn(
        "flex flex-row items-center justify-start overflow-hidden whitespace-nowrap text-ellipsis rounded-md bg-gray-100 border border-gray-200 text-gray-800",
        variantClasses.container,
        className,
      )}
    >
      <Loader
        className={cn(variantClasses.icon, "flex-shrink-0")}
        aria-hidden="true"
      />
      {t("invoice_statuses.exporting")}
    </div>
  );
};

type ExportFailedProps = Props & {
  hasWarning?: boolean;
};

export const ExportFailedIcon = ({
  hasWarning = false,
  className,
  variant = "md",
}: ExportFailedProps) => {
  const { t } = useTranslation();
  const variantClasses = getVariantClasses(variant);

  return (
    <div className="flex flex-row items-center gap-2">
      <div
        className={cn(
          "flex flex-row items-center justify-start overflow-hidden whitespace-nowrap text-ellipsis rounded-md bg-red-100 border border-red-200 text-red-800",
          variantClasses.container,
          className,
        )}
      >
        <X
          className={cn(variantClasses.icon, "flex-shrink-0")}
          aria-hidden="true"
        />
        {t("invoice_statuses.export_failed")}
      </div>
      {hasWarning && (
        <div className="[&>svg]:text-destructive">
          <WarningIcon className="inline-block" size={26} />
        </div>
      )}
    </div>
  );
};

export const ExportedIcon = ({ className, variant = "md" }: Props) => {
  const { t } = useTranslation();
  const variantClasses = getVariantClasses(variant);

  return (
    <div
      className={cn(
        "flex flex-row items-center justify-start overflow-hidden whitespace-nowrap text-ellipsis rounded-md bg-indigo-100 border border-indigo-200 text-indigo-800",
        variantClasses.container,
        className,
      )}
    >
      <CheckCircle2
        className={cn(variantClasses.icon, "flex-shrink-0")}
        aria-hidden="true"
      />
      {t("invoice_statuses.exported")}
    </div>
  );
};

export const ErrorIcon = ({ className, variant = "md" }: Props) => {
  const { t } = useTranslation();
  const variantClasses = getVariantClasses(variant);

  return (
    <div
      className={cn(
        "flex flex-row items-center justify-start overflow-hidden whitespace-nowrap text-ellipsis rounded-md bg-red-100 border border-red-200 text-red-800",
        variantClasses.container,
        className,
      )}
    >
      <X
        className={cn(variantClasses.icon, "flex-shrink-0")}
        aria-hidden="true"
      />
      {t("invoice_statuses.error")}
    </div>
  );
};

export const WaitingForClientApprovalIcon = ({
  className,
  variant = "md",
}: Props) => {
  const { t } = useTranslation();
  const variantClasses = getVariantClasses(variant);

  return (
    <div
      className={cn(
        "flex flex-row items-center justify-start overflow-hidden whitespace-nowrap text-ellipsis rounded-md bg-gray-100 border border-gray-200 text-gray-800",
        variantClasses.container,
        className,
      )}
    >
      <Hourglass
        className={cn(variantClasses.icon, "flex-shrink-0")}
        aria-hidden="true"
      />
      {t("invoice_statuses.waiting_for_client_approval")}
    </div>
  );
};

export const getStatusBadge = (
  status: InvoiceStatus,
  variant: StatusIconVariant = "md",
) => {
  switch (status) {
    case InvoiceStatus.PREPROCESSED:
      return <ToReviewIcon className="flex-shrink-0" variant={variant} />;
    case InvoiceStatus.NEW:
      return <ProcessingIcon className="flex-shrink-0" variant={variant} />;
    case InvoiceStatus.ERROR:
      return <ErrorIcon className="flex-shrink-0" variant={variant} />;
    default:
      return null;
  }
};
